import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_TARGET_DESK_ID,
  FindOrdersTableDefinitionFactory,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  COLUMN_ORDER_DEALT_FWD_PTS,
  SystemConfigPromiseService,
  Roles,
  CurrencyPairGroupLookupService,
} from "shared/cubiq-services";
import { FindActionPrimaryService } from "../../shared/cubiq-services/action/impl/FindActionPrimaryService";

import { BfxFind } from "../bfx-find/bfx-find";
import { ElementRef, ViewChild, ChangeDetectorRef } from "@angular/core";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxFindOrdersContextMenuService } from "../bfx-find-orders/bfx-find-orders-contextmenu.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { LogoutNotificationService } from "../bfx-auth/logout-notification.service";
import { BfxFindOrdersFilterViewComponent } from "./bfx-find-orders-filter-view.component";
import { BfxFindOrdersService } from "../bfx-find-orders/bfx-find-orders.service";
import { BfxBaseBlotter } from "app/bfx-base-blotter/bfx-base-blotter";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

import {
  COLUMN_ORDER_FWD_DEALT_TRACKING,
  COLUMN_ORDER_BASE_FWD_PTS,
  COLUMN_ORDER_FWD_BASE_TRACKING,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";

/**
 * Post-processed OrderData type needed for the blotter
 */
export interface ExtendedOrderData extends CubiqTS.OrderData {
  executedPerc: number;
  risk: BmarkRiskData;
}

@Component({
  selector: `bfx-find-orders`,
  templateUrl: "./html/bfx-find-orders.html",
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxFindOrdersComponent
  extends BfxFind<CubiqTS.OrderDataImpl, FindOrdersTableDefinitionFactory>
  implements OnInit, OnDestroy, GoldenLayoutComponent
{
  @ViewChild("embeddedBlotter", { static: true })
  public embeddedBlotter: BfxBaseBlotter;

  @ViewChild("filterView", { static: true })
  protected filterView: BfxFindOrdersFilterViewComponent;

  protected embeddedOrderColumnAllowListOverrideConfig: Map<string, object> = new Map<
    string,
    object
  >([
    [
      "dealtPercentAway",
      {
        visible: false,
        omitFromPicker: true,
      },
    ],
    [
      "basePercentAway",
      {
        visible: false,
        omitFromPicker: true,
      },
    ],
    [
      "dealtSpotMarketMid",
      {
        visible: false,
        omitFromPicker: true,
      },
    ],
    [
      "baseSpotMarketMid",
      {
        visible: false,
        omitFromPicker: true,
      },
    ],
    [
      COLUMN_ORDER_DEALT_TRACKING,
      {
        visible: false,
        omitFromPicker: true,
      },
    ],
    [
      COLUMN_TARGET_DESK_ID,
      {
        visible: false,
        omitFromPicket: true,
      },
    ],
    [
      COLUMN_ORDER_DEALT_FWD_PTS,
      {
        visible: false,
        omitFromPicket: true,
      },
    ],
    [
      COLUMN_ORDER_FWD_DEALT_TRACKING,
      {
        visible: false,
        omitFromPicket: true,
      },
    ],
    [
      COLUMN_ORDER_BASE_FWD_PTS,
      {
        visible: false,
        omitFromPicket: true,
      },
    ],
    [
      COLUMN_ORDER_FWD_BASE_TRACKING,
      {
        visible: false,
        omitFromPicket: true,
      },
    ],
  ]);
  protected readonly profileKey: string = `${ProfileSettingKeys.userProfileFindBlotter}/${
    CubiqTS.BlotterType[CubiqTS.BlotterType.ORDER]
  }`;
  protected readonly profileVersion: string = `2018091102`;
  public canExport: boolean = false;

  constructor(
    _clipboardService: BfxClipboardService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    cd: ChangeDetectorRef,
    ngZone: NgZone,
    elementRef: ElementRef,
    goldenLayoutService: GoldenLayoutService,
    public contextMenuService: BfxFindOrdersContextMenuService,
    profileActionService: ProfilePromiseActionService,
    logoutNotificationService: LogoutNotificationService,
    findService: BfxFindOrdersService,
    protected findActionService: FindActionPrimaryService,
    //
    tableDefinitionFactory: FindOrdersTableDefinitionFactory,
    protected userProfileService: BfxUserProfileService,
    protected systemConfigService: SystemConfigPromiseService,
    protected currencyPairGroupService: CurrencyPairGroupLookupService,
  ) {
    super(
      _clipboardService,
      cd,
      ngZone,
      elementRef,
      goldenLayoutService,
      findService,
      contextMenuService,
      profileActionService,
      logoutNotificationService,
      userProfileService,
      tableDefinitionFactory,
      currencyPairGroupService,
    );

    systemConfigService
      .hasRole(Roles.ROLE_ORDER_EXPORT)
      .then((hasRole) => (this.canExport = hasRole));

    this.initSearchClickHandler();
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.embeddedBlotter != null) {
      this.embeddedBlotter.blotterId = "bfx-find-orders";
    }
  }

  initSearchClickHandler() {
    this.filterViewResponseObservableSub = this.findService
      .getFilterViewResponseObservable()
      .subscribe((data) => {
        this.findActionService.findOrders(this.transformData(data)).then(
          (response) => {
            logger.out.info("FindOrders: Showing search results");
            this.inProgress = false;
            let a = response as any;
            this.captionLabel = a.captionLabel;
            const extendedResults = this.postProcessResults(a.results);
            this.showResults(extendedResults);
            logger.out.info("FindOrders: Showing search results");
          },
          (err) => {
            this.inProgress = false;
            this.captionLabel = err?.toString() ?? `Unknown error`;
            logger.out.info(`FindOrders: Error processing search results ${this.captionLabel}`);
            this.showResults([]);
          },
        );
      });
  }

  /**
   * Add additional columns that are required for the Base table columns - currently only
   * executedPerc property is required
   *
   * @param orders
   * @returns
   */
  private postProcessResults(orders: CubiqTS.OrderDataImpl[]): ExtendedOrderData[] {
    if (orders) {
      return orders.map((order) => this.createExtendedOrder(order));
    }
    return [];
  }

  /**
   * Add required derived fields to the basic OrderData
   * @param order
   * @returns
   */
  protected createExtendedOrder(order: CubiqTS.OrderData): ExtendedOrderData {
    let filled = order.executedQty;

    if (order.leavesQty != null && order.orderQty != null) {
      order.orderQty.minus(order.leavesQty);
    }
    let fillPercent: number = NaN;
    if (filled != null && order.orderQty != null && !order.orderQty.isZero()) {
      fillPercent = Number(filled.dividedBy(order.orderQty)) * 100;
    }
    let risk: BmarkRiskData = null;
    if (order.orderType === CubiqTS.OrderType.BENCHMARK && order.allocationData?.length > 1) {
      risk = new BmarkRiskData(order, order.allocationData);
    }

    const retOrder: ExtendedOrderData = { executedPerc: fillPercent, risk, ...order };
    return retOrder;
  }
}
